import { useCallback, useEffect, useState } from 'react';
import { fetchRemoteCashActivity } from '../../../reports/infra/reportsRemoteRepo';
import type { CashActivityItem } from '../../../reports/infra/reportsRemoteRepo';
import type { CashSessionRow } from '../../infra/cashSessionsRepo';
import { fetchRemoteOutflowsForSession, type CashOutflowRow } from '../../infra/cashOutflowRepo';
import { isOnline } from '../../../shell/connectivity/connectivityInfra';
import { captureCatch } from '../../../shared/sentry/sentryUser';

type Params = {
  session: CashSessionRow | null;
  branchId: string | null;
  enabled: boolean;
};

export function outflowToActivityItems(sessionId: string, row: CashOutflowRow): CashActivityItem[] {
  const items: CashActivityItem[] = [
    {
      kind: 'outflow_create',
      id: row.id,
      sessionId,
      occurredAt: row.createdAt,
      amount: row.amount,
      category: row.category,
      note: row.note,
      actorUserId: row.createdBy,
      status: row.status,
      voidReason: null,
      direction: row.direction,
    },
  ];
  if (row.status === 'voided' && row.voidedAt) {
    items.push({
      kind: 'outflow_void',
      id: row.id,
      sessionId,
      occurredAt: row.voidedAt,
      amount: row.amount,
      category: row.category,
      note: row.note,
      actorUserId: row.voidedBy,
      status: 'voided',
      voidReason: row.voidReason,
      direction: row.direction,
    });
  }
  return items;
}

export function buildLocalSessionActivity(session: CashSessionRow, outflows: CashOutflowRow[]): CashActivityItem[] {
  const items: CashActivityItem[] = [
    {
      kind: 'cash_open',
      id: session.id,
      sessionId: session.id,
      occurredAt: session.openedAt,
      amount: session.openingAmount,
      category: null,
      note: null,
      actorUserId: session.openedBy ?? null,
      status: session.status,
      voidReason: null,
      direction: null,
    },
  ];

  for (const row of outflows) {
    items.push(...outflowToActivityItems(session.id, row));
  }

  if (session.status === 'closed' && session.closedAt) {
    items.push({
      kind: 'cash_close',
      id: session.id,
      sessionId: session.id,
      occurredAt: session.closedAt,
      amount: session.closingAmount ?? null,
      category: null,
      note: session.note ?? null,
      actorUserId: session.closedBy ?? null,
      status: 'closed',
      voidReason: null,
      direction: null,
    });
  }

  return items.sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
}

async function loadRemoteSessionActivity(
  branchId: string,
  session: CashSessionRow
): Promise<CashActivityItem[] | null> {
  const remote = await fetchRemoteCashActivity(branchId, {
    since: session.openedAt,
    limit: 100,
    offset: 0,
  });
  if (!remote) return null;
  return remote.items
    .filter((item) => item.sessionId === session.id)
    .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
}

export function useCashSessionMovements({ session, branchId, enabled }: Params) {
  const [items, setItems] = useState<CashActivityItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const reload = useCallback(async () => {
    if (!session || !enabled) {
      setItems([]);
      setLoadError(false);
      return;
    }

    setLoading(true);
    try {
      const outflows = await fetchRemoteOutflowsForSession(session.id);
      if (outflows == null) {
        // Fallo remoto: conservar apertura/cierre del turno (datos ya cargados
        // con la sesión) y marcar error para que la UI ofrezca reintento.
        setItems(buildLocalSessionActivity(session, []));
        setLoadError(true);
        return;
      }
      let next = buildLocalSessionActivity(session, outflows);

      const outflowCount = outflows.length;
      const hasClose = session.status === 'closed' && session.closedAt;
      const needsRemote = outflowCount === 0 && hasClose;

      if (needsRemote && branchId) {
        const online = await isOnline();
        if (online) {
          const remote = await loadRemoteSessionActivity(branchId, session);
          if (remote && remote.length > 0) {
            next = remote;
          }
        }
      }

      setItems(next);
      setLoadError(false);
    } catch (error) {
      // BUG-11: catchear errores para evitar unhandled promise rejection
      captureCatch('cash', 'useCashSessionMovements.reload')(error);
      setItems(buildLocalSessionActivity(session, []));
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [session, enabled, branchId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { items, loading, loadError, reload };
}
